/* Park Friends: shipping protection, folded into the checkout button.
   ---------------------------------------------------------------------------------------
   David's reference, 2026-09-30:

       [  Protected checkout | $76.80  ]
          Protection from damage, loss and theft for $1.80   (i) Learn more
          Checkout without shipping protection

   The money is the Easy Protection app's, never ours. Probed on the live theme the same day:
   the app puts its own card in the drawer (.easy-insurance), prices at 2.5% of the cart snapped
   to its variant ladder, and writes nothing to the cart until the checkout button is pressed,
   at which point it POSTs /cart/update.js?easy=addins with the variant matching its fee. So
   this file:

     1. reads the fee off the app's own card
     2. adds it to the total already on the button and repaints the button
     3. keeps the app's switch in the state the button is claiming
     4. lets the ordinary form submit run, so the app's own handler does the add

   Nothing here touches /cart. If the app is absent the block stays hidden and the plain Secure
   checkout button is untouched, which is also what happens if any of the pieces are missing.

   Everything is re-run on updated.ecomify.cart and on a mutation, because assets/cart.js
   replaces .offcanvas-body and .offcanvas-footer wholesale on every cart change and the app
   re-injects its card after that. */
(function () {
  'use strict';

  var WIDGET = '.easy-insurance';

  function parseCents(s) {
    if (!s) return null;
    var m = String(s).replace(/[ \s]/g, '').match(/(\d[\d.,]*)/);
    if (!m) return null;
    var n = m[1];
    /* "1.234,56" and "1,234.56" both appear in the wild. Whichever separator comes last is the
       decimal one; anything else is grouping. */
    var lastDot = n.lastIndexOf('.');
    var lastComma = n.lastIndexOf(',');
    var dec = Math.max(lastDot, lastComma);
    var whole, frac;
    if (dec > -1 && n.length - dec - 1 <= 2) {
      whole = n.slice(0, dec).replace(/[.,]/g, '');
      frac = n.slice(dec + 1);
    } else {
      whole = n.replace(/[.,]/g, '');
      frac = '';
    }
    while (frac.length < 2) frac += '0';
    var v = parseInt(whole || '0', 10) * 100 + parseInt(frac.slice(0, 2), 10);
    return isFinite(v) ? v : null;
  }

  /* Formats by reusing the string the page already printed, so the currency symbol, its side
     and the separators come from Shopify rather than from a guess made here. */
  function formatLike(cents, sample) {
    var m = String(sample || '').match(/(\d[\d.,]*)/);
    if (m == null) return String(cents / 100);
    var n = m[1];
    var lastDot = n.lastIndexOf('.');
    var lastComma = n.lastIndexOf(',');
    var dec = Math.max(lastDot, lastComma);
    var decSep = dec > -1 && n.length - dec - 1 <= 2 ? n[dec] : '.';
    var grpSep = decSep === '.' ? ',' : '.';
    var hasGroup = n.slice(0, dec > -1 ? dec : n.length).indexOf(grpSep) > -1 || parseCents(n) >= 100000;
    var whole = String(Math.floor(Math.abs(cents) / 100));
    var frac = String(Math.abs(cents) % 100);
    if (frac.length < 2) frac = '0' + frac;
    if (hasGroup) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, grpSep);
    return sample.replace(m[1], whole + decSep + frac);
  }

  function widgetIn(root) { return root.querySelector(WIDGET); }

  /* Writing textContent replaces the text node, which the MutationObserver below sees, which
     schedules another pass. Same string, same write, forever. So nothing is written unless it
     actually changes. */
  function setText(el, value) {
    if (!el) return;
    if (el.textContent.trim() !== value) el.textContent = value;
  }

  /* THE TOTAL COMES FROM /cart.js, NOT FROM THE RENDER.
     The server figure on the block is right the instant the footer is drawn and can be stale a
     second later: the protection app deletes a leftover protection line of its own accord after
     the page has rendered. Caught on the cart page 2026-09-30, where the button briefly read
     $78.60 on a cart the till had at $76.80, because the block still carried the old 7680 and
     the fee went on top of it. Read the cart, and if a protection line is already sitting in it
     the fee is in the total already and must not be added twice. */
  var live = { total: null, protect: 0 };
  var reading = false;
  function readCart() {
    if (reading) return;
    reading = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        reading = false;
        if (!j) return;
        var handles = (document.querySelector('[data-pf-protect]') || { dataset: {} }).dataset.pfProtectHandles || '';
        var list = handles.split(',').map(function (h) { return h.trim().toLowerCase(); }).filter(Boolean);
        var p = 0;
        (j.items || []).forEach(function (i) {
          if (list.indexOf(String(i.handle || '').toLowerCase()) > -1) p += i.final_line_price;
        });
        live.total = j.total_price;
        live.protect = p;
        applyAll();
      })
      .catch(function () { reading = false; });
  }

  function feeOf(widget) {
    var el = widget && widget.querySelector('.easy-price');
    return el ? parseCents(el.textContent) : null;
  }

  function switchOf(widget) { return widget && widget.querySelector('.easy-switch-input-btn'); }

  /* The app's control, driven by its own click handler so its state, its variant pick and its
     checkout hook all stay its business. */
  function setProtection(widget, on) {
    var sw = switchOf(widget);
    if (!sw) return false;
    if (!!sw.checked === !!on) return true;
    sw.click();
    return true;
  }

  function apply(block) {
    var form = block.closest('form');
    if (!form) return;
    var root = form.closest('#offcanvas-cart') || form.closest('#cart') || document;
    var widget = widgetIn(root) || widgetIn(document);
    var totalEl = document.getElementById(block.dataset.pfProtectTotal);
    var cta = form.querySelector('button[name="checkout"]');
    var fee = feeOf(widget);

    if (!widget || !cta || fee == null || !totalEl) {
      block.hidden = true;
      return;
    }

    /* What the till has right now. The server figure is only the fallback. */
    var cartTotal = live.total;
    if (cartTotal == null) cartTotal = Number(block.dataset.pfProtectCents);
    if (!isFinite(cartTotal)) cartTotal = parseCents(totalEl.textContent);
    if (cartTotal == null) { block.hidden = true; return; }

    if (!block.dataset.pfProtectSample) block.dataset.pfProtectSample = totalEl.textContent.trim();
    var sample = block.dataset.pfProtectSample;

    if (block.dataset.pfProtectReady !== '1') {
      setProtection(widget, block.dataset.pfProtectDefault !== 'off');
      block.dataset.pfProtectReady = '1';
    }
    var on = !!(switchOf(widget) && switchOf(widget).checked);
    var already = live.protect > 0;

    var shown = cartTotal;
    if (on && !already) shown = cartTotal + fee;
    if (!on && already) shown = cartTotal - live.protect;
    setText(totalEl, formatLike(shown, sample));

    var label = cta.querySelector('.pf-cart__cta-label');
    setText(label, on ? block.dataset.pfProtectLabelOn : block.dataset.pfProtectLabelOff);

    var desc = block.querySelector('[data-pf-protect-desc]');
    if (desc) setText(desc, (desc.dataset.pfProtectDescTpl || '').replace('[fee]', formatLike(already ? live.protect : fee, sample)));

    /* With protection off there is nothing under the button worth the space: the CTA has gone
       back to plain Secure checkout and the plain total. That state only shows if the owner
       turns the default off, because clicking the link navigates. */
    block.hidden = !on;
  }

  function applyAll() {
    document.querySelectorAll('[data-pf-protect]').forEach(apply);
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    var info = t.closest('[data-pf-protect-info]');
    if (info) {
      var block = info.closest('[data-pf-protect]');
      var more = block && block.querySelector('[data-pf-protect-more]');
      if (more) {
        more.hidden = !more.hidden;
        info.setAttribute('aria-expanded', more.hidden ? 'false' : 'true');
      }
      return;
    }

    var plain = t.closest('[data-pf-protect-plain]');
    if (plain) {
      event.preventDefault();
      var b = plain.closest('[data-pf-protect]');
      var form = b && b.closest('form');
      var root = form && (form.closest('#offcanvas-cart') || form.closest('#cart'));
      var widget = (root && widgetIn(root)) || widgetIn(document);
      setProtection(widget, false);
      readCart();
      applyAll();
      /* the app reads its switch on the checkout click, so give it the tick it needs to have
         written the new state before the form goes */
      setTimeout(function () {
        var cta = form && form.querySelector('button[name="checkout"]');
        if (cta) cta.click();
        else if (form) form.submit();
      }, 350);
    }
  });

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; applyAll(); });
  }

  /* The app paints its fee a moment after the card lands, so a couple of settling passes stop
     the button showing the unprotected total for half a second. */
  function settle() {
    readCart();
    [0, 250, 700, 1600, 3000].forEach(function (ms) { setTimeout(applyAll, ms); });
    setTimeout(readCart, 1200);
  }

  function watch() {
    var host = document.querySelector('#offcanvas-cart-wrapper') || document.body;
    if (!host) return;
    new MutationObserver(schedule).observe(host, { childList: true, subtree: true });
    var page = document.querySelector('#cart');
    if (page) new MutationObserver(schedule).observe(page.parentNode || page, { childList: true, subtree: true });
  }

  window.addEventListener('updated.ecomify.cart', settle);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { watch(); settle(); });
  } else {
    watch();
    settle();
  }
})();

/* Chat launcher off while the drawer is open (2026-10-23).
   Our n8n chat widget mounts #chatbridge-host bottom right with inline !important styles (so no
   stylesheet can move or hide it) and at phone width its bubble sat on the total of the SECURE
   CHECKOUT button, and its teaser on the "You save" line. While the drawer is open the shopper is
   paying, not chatting: the host is hidden on show.bs.offcanvas and restored on hidden.bs.offcanvas,
   through the same inline !important the widget uses, so the widget's own guard is not fought. */
(function () {
  var cart = document.getElementById('offcanvas-cart');
  if (!cart) return;
  function host() { return document.getElementById('chatbridge-host'); }
  cart.addEventListener('show.bs.offcanvas', function () { var h = host(); if (h) h.style.setProperty('display', 'none', 'important'); });
  cart.addEventListener('hidden.bs.offcanvas', function () { var h = host(); if (h) h.style.setProperty('display', 'block', 'important'); });
})();
